import {
  RobotoConflictException,
  RobotoDomainException,
  RobotoNotFoundException,
  RobotoUnauthorizedException,
  shouldReportError,
} from "@/shared/http";
import { LoggerService } from "@/shared/services/LoggerService";

/**
 * Whether a failed request means there is no board at that id this person can
 * open: a board that does not exist, or a teammate's personal board. The
 * platform answers both with 404 so that a read cannot tell someone that a
 * board they may not open exists, and a caller names both possibilities.
 *
 * The 404 arrives as a `RobotoNotFoundException`, or, when the response names
 * no exception class and several classes use 404, as a `RobotoDomainException`
 * whose `errorCode` is `"404"`.
 */
export function isBoardMissingOrUnavailable(error: unknown): boolean {
  if (error instanceof RobotoNotFoundException) {
    return true;
  }
  return error instanceof RobotoDomainException && error.errorCode === "404";
}

/**
 * What to tell a person about a board request that did not complete: `failed`,
 * a sentence saying what did not happen ("The board was not saved."), then what
 * stopped it and what to do, in words the person acts on. The platform's own
 * words, which can name the board's id or the request's fields, go to the
 * console only.
 */
export function boardRequestFailureMessage(
  failed: string,
  error: unknown,
): string {
  LoggerService.warn(failed, error);
  return `${failed} ${boardRequestFailureReason(error)}`;
}

/**
 * What stopped a board request and what to do, as the second sentence of
 * {@link boardRequestFailureMessage}, for a place whose heading already says
 * what did not happen.
 */
export function boardRequestFailureReason(error: unknown): string {
  if (isBoardMissingOrUnavailable(error)) {
    return "The board no longer exists, or it is no longer shared with you.";
  }
  if (error instanceof RobotoUnauthorizedException) {
    return "You do not have permission to do this.";
  }
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return "Roboto rejected the request. Reload the page and try again.";
  }
  return "Try again in a moment.";
}

/**
 * Whether a failed board request is worth reporting to error monitoring.
 *
 * Narrower than `shouldReportError` by two answers a person acts on rather than
 * reads as a fault:
 *
 * - A conflict (`RobotoConflictException`), such as a name already in use.
 *   Names are unique per owner, so two people naming their boards the same
 *   thing is ordinary, and the person picks another name.
 * - A write refused for access (`RobotoUnauthorizedException`). Who may change
 *   or delete a board depends on its owner and the person's role
 *   (`boardPermissions`), so a refusal is the platform applying that rule.
 *
 * `shouldReportError` keeps reporting both, because the global query-error
 * handler and the metrics dashboard and session hooks also call it.
 */
export function shouldReportBoardError(error: unknown): boolean {
  if (
    error instanceof RobotoConflictException ||
    error instanceof RobotoUnauthorizedException
  ) {
    return false;
  }
  return shouldReportError(error);
}
